"use client";

import {
  THEATER_RAIL_TITLE_SIDE_FIT_MARGIN_PX,
  doesTheaterRailTitleFit,
  getGameTheaterRailTitleClass,
  getTheaterRailTitleAvailableBox,
  getTheaterRailTitleSlotClass,
} from "@/lib/constants/game-theater-chrome";
import { cn } from "@/lib/utils";
import { useLayoutEffect, useRef, useState } from "react";

/**
 * Game name in the chrome bar: rotated on the side rail, horizontal on the
 * portrait bottom bar. Hidden while the full title would clip or overlap
 * other chrome.
 */
export function TheaterRailTitle({ title }: { title: string }) {
  const slotRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLParagraphElement>(null);
  const [fits, setFits] = useState(false);

  useLayoutEffect(() => {
    const slot = slotRef.current;
    const text = textRef.current;
    if (!slot || !text) return;

    const update = () => {
      const box = getTheaterRailTitleAvailableBox(slot);
      const extra = box.layout === "column" ? THEATER_RAIL_TITLE_SIDE_FIT_MARGIN_PX : 0;
      setFits(doesTheaterRailTitleFit(text, box, extra));
    };

    update();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(update);
    observer.observe(slot);
    observer.observe(text);
    const parent = slot.parentElement;
    if (parent) {
      observer.observe(parent);
      for (const child of parent.children) {
        observer.observe(child);
      }
    }
    return () => observer.disconnect();
  }, [title]);

  const trimmed = title.trim();
  if (!trimmed) return null;

  return (
    <div
      ref={slotRef}
      data-testid="theater-rail-title"
      data-fits={fits ? "true" : "false"}
      className={getTheaterRailTitleSlotClass(fits)}
    >
      <p
        ref={textRef}
        aria-hidden="true"
        title={fits ? trimmed : undefined}
        data-testid="theater-rail-title-text"
        className={cn(
          "whitespace-nowrap text-2xl font-bold leading-none text-white/80",
          getGameTheaterRailTitleClass(),
          !fits && "pointer-events-none opacity-0"
        )}
      >
        {trimmed}
      </p>
    </div>
  );
}
